import { Component, OnDestroy, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, filter } from 'rxjs';
import { AppShieldSensitivityPickerComponent, DDoSShieldSensitivity, DocumentationComponent, WafMappedEnumList } from '@bn/app-shield';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { DashboardSharedModule } from '@bn/dashboard-shared';
import { ThemeService } from '@bn/dashboard-shared/services';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { UserTheme } from '@bn/shared/entities/enums';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartDataGranularity } from '@bn/web-ui/chart';
import { ChartMultiMetricComponent } from '@bn/web-ui/chart/components/charts/chart-multi-metric/chart-multi-metric.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { LinkListItem } from '@bn/web-ui/link-list/models';
import { SelectItem } from '@bn/web-ui/select/models';
import { SelectComponent } from '@bn/web-ui/select/select.component';
import { SensitivityLevelTemplate } from '../../components/app-shield-sensitivity-picker/models';
import { DDoSExecutionModeEnum, PlanType } from '../../enums';
import { MapWafEnumToFormListItems } from '../../pipes';
import { ShieldApiStore } from '../../stores';
import { ShieldDdosChartData, mapShieldDdosChartData } from '../../stores/helpers/shield-api-store.helper';

@Component({
  templateUrl: './app-shield-ddos.page.html',
  styleUrls: ['./app-shield-ddos.page.scss'],
  imports: [
    DashboardSharedModule,
    DocumentationComponent,
    AppShieldSensitivityPickerComponent,
    CardComponent,
    SelectComponent,
    ChartMultiMetricComponent,
    FaIconComponent,
    ReactiveFormsModule,
  ],
})
export class AppShieldDdosPage extends AbstractLazyDirective implements OnDestroy {
  private readonly dialog = inject(BsModalService);

  readonly #shieldStore = inject(ShieldApiStore);
  readonly dataGranularity = signal<ChartDataGranularity>('daily');
  readonly selectedShield = this.#shieldStore.selectedEntity();
  public isLoadingChallengeWindow = this.#shieldStore.isLoading(StoreRequest.Update, ['ddosChallengeWindow']);
  public isLoadingSensitivity = this.#shieldStore.isLoading(StoreRequest.Update, ['ddosSensitivity']);
  public isLoadingMode = this.#shieldStore.isLoading(StoreRequest.Update, ['ddosShieldMode']);

  public readonly contentService = inject(ContentService);
  public readonly theme$: Observable<UserTheme>;

  public shieldExecutionModes!: WafMappedEnumList;
  public dDoSChallengeWindowItems: SelectItem[] = [];
  public fb = inject(NonNullableFormBuilder);
  public ddosConfigForm = this.fb.group({
    dDoSExecutionMode: this.fb.control(DDoSExecutionModeEnum.Logging),
    dDoSChallengeWindow: this.fb.control(0),
  });
  public dDoSShieldSensitivity!: number;

  public levelTemplates = [
    {
      id: DDoSShieldSensitivity.Off,
      isActive: false,
      title: 'Lowest',
      description: 'Blocks only the largest and most disruptive DDoS floods.',
      illustration: '/assets/images/bunny-shield-protection-level-asleep.svg',
    },
    {
      id: DDoSShieldSensitivity.Low,
      isActive: false,
      title: 'Low',
      description: 'Filters moderate anomalies, maintaining high accessibility.',
      illustration: '/assets/images/bunny-shield-protection-level-low.svg',
    },
    {
      id: DDoSShieldSensitivity.Medium,
      isActive: false,
      title: 'Medium',
      description: 'Provides robust defense against common DDoS threats with minimal disruption.',
      illustration: '/assets/images/bunny-shield-protection-level-medium.svg',
    },
    {
      id: DDoSShieldSensitivity.High,
      isActive: false,
      title: 'High',
      description: 'Aggressively blocks suspicious traffic in elevated-risk scenarios.',
      illustration: '/assets/images/bunny-shield-protection-level-high.svg',
    },
    {
      id: DDoSShieldSensitivity.Challenge,
      isActive: false,
      title: 'Extreme',
      description: 'Uses JavaScript challenges to neutralize bots and attacks.',
      illustration: '/assets/images/bunny-shield-protection-level-extreme.svg',
    },
  ];

  public blockedRequests = signal(0);
  public challengedRequests = signal(0);
  public loggedRequests = signal(0);
  public verifiedRequests = signal(0);
  public threatActivityChartHasData = signal(false);
  public threatActivityChart = signal<ShieldDdosChartData>(mapShieldDdosChartData());

  public titleFirst = 'Documentation';
  public linksFirst?: LinkListItem[] = [
    { text: 'Overview', url: 'https://bunny.net/docs/shield' },
    { text: 'Troubleshooting', url: 'https://bunny.net/docs/shield/troubleshooting' },
    { text: 'API Specifications', url: 'https://bunny.net/docs/api-reference/shield' },
  ];

  constructor() {
    super();

    const pullZoneId = this.#shieldStore.pullZoneId();

    this.contentService.setTitle('DDoS mitigation');
    this.contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.theme$ = inject(ThemeService).getCurrentTheme();

    this.setExecutionModes();
    this.setDdosEngineConfig();
    this.patchDDoSEngineConfig();
    this.subscribeToChallengeWindowChanges();
    this.subscribeToStoreSignals();

    effect(() => {
      this.dDoSShieldSensitivity = this.selectedShield()?.dDoSShieldSensitivity ?? 0;

      this.setDDoSSensitivity(this.dDoSShieldSensitivity);
    });

    this.ddosConfigForm.get('dDoSExecutionMode')?.valueChanges.subscribe((value) => {
      this.onEngineTabChange(value);
    });
  }

  public ngOnDestroy(): void {
    this.contentService.setBackButton(null);
    this.contentService.setBadge(null);
  }

  public subscribeToChallengeWindowChanges(): void {
    const challengeWindowControl = this.ddosConfigForm.get('dDoSChallengeWindow');
    if (challengeWindowControl) {
      this.subscriptions.add(
        challengeWindowControl.valueChanges.subscribe((newValue) => {
          const id = this.selectedShield()?.shieldZoneId;
          const oldValue = this.selectedShield()?.dDoSChallengeWindow;
          if (!id || oldValue === newValue) return;

          this.#shieldStore.updateShield({
            id,
            data: { dDoSChallengeWindow: newValue },
            customFields: ['ddosChallengeWindow'],
          });
        }),
      );
    }
  }

  private subscribeToStoreSignals(): void {
    toObservable(this.#shieldStore.chartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        const { blockedRequests, challengedRequests, loggedRequests, verifiedRequests } = chartMetrics.ddosChartData.total;

        this.threatActivityChart.set(chartMetrics.ddosChartData);
        this.threatActivityChartHasData.set(chartMetrics.ddosChartData.hasData || false);

        this.blockedRequests.set(blockedRequests);
        this.challengedRequests.set(challengedRequests);
        this.loggedRequests.set(loggedRequests);
        this.verifiedRequests.set(verifiedRequests);
      });

    toObservable(this.selectedShield)
      .pipe(filter((shield) => !!shield))
      .pipe(takeUntilDestroyed())
      .subscribe((shield) => {
        const plan = shield?.planType || 0;

        if (plan > PlanType.Basic) {
          const planName = PlanType[plan];

          this.contentService.setBadge({
            contextType: 'premium',
            text: planName,
            tooltip: `You have Bunny Shield ${planName} enabled`,
          });
        } else {
          this.contentService.setBadge(null);
        }
      });
  }

  public onSelectDDoSSensitivity({ item }: { item: SensitivityLevelTemplate }): void {
    const sensitivityIndex = item.id as DDoSShieldSensitivity;

    const id = this.selectedShield()?.shieldZoneId;
    if (!id || this.selectedShield()?.dDoSShieldSensitivity === sensitivityIndex) return;

    this.#shieldStore.updateShield({
      id,
      data: { dDoSShieldSensitivity: sensitivityIndex },
      toastr: {
        message: `DDoS sensitivity was set to <strong>${this.levelTemplates[sensitivityIndex].title}</strong>.`,
      },
      customFields: ['ddosSensitivity'],
    });
  }

  public setDDoSSensitivity(sensitivityIndex: number): void {
    this.levelTemplates.map((t) => (t.isActive = false));

    const activeItem = this.levelTemplates.find((item) => item.id === sensitivityIndex);
    if (activeItem) {
      activeItem.isActive = true;
    }
  }

  private setExecutionModes(): void {
    toObservable(this.#shieldStore.ddosEnums).subscribe((ddosEnums) => {
      this.shieldExecutionModes = ddosEnums?.find((apiEnum) => apiEnum.enumName === 'DDoSExecutionMode') ?? <WafMappedEnumList>[];
    });
  }

  private setDdosEngineConfig(): void {
    effect(() => {
      const ddosEnum = this.#shieldStore.ddosEnums().find(({ enumName }) => enumName === 'DDoSChallengeWindowType');

      this.dDoSChallengeWindowItems =
        ddosEnum?.enumValues
          ?.filter(({ name, value }) => name !== null && name !== undefined && value !== undefined) // Remove invalid entries
          .map(({ name, value }) => ({
            Name: name as string,
            Id: value as number,
          })) || [];
    });
  }

  public onEngineTabChange(value: DDoSExecutionModeEnum): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    if (value === DDoSExecutionModeEnum.Logging) {
      this.handleConfirmEngineChange(shieldId, value);

      return;
    }

    this.#shieldStore.updateShield({
      id: +shieldId,
      data: {
        dDoSExecutionMode: value,
      },
      customFields: ['ddosShieldMode'],
      toastr: {
        message: `DDoS Mitigation Mode changed to <strong>${
          new MapWafEnumToFormListItems().transform(this.shieldExecutionModes).find((formListItem) => formListItem.value === value)?.name
        }</strong>.`,
      },
    });
  }

  private handleConfirmEngineChange(shieldId: string | number, value: DDoSExecutionModeEnum): void {
    const confirmDialogRef = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          icon: 'file-shield',
          confirmButtonName: 'Yes, Continue',
          description:
            'By switching to <strong>logging mode</strong> we will not block detected DDoS attacks, this can be done if you`re seeing false positives, however if under attack your origin may go offline.',
          event: {
            action: 'confirm',
          },
          title: 'Turn on logging mode?',
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      confirmDialogRef.content?.getOnAction$().subscribe((event) => {
        if (event.action === 'cancel') {
          this.ddosConfigForm.get('dDoSExecutionMode')?.setValue(DDoSExecutionModeEnum.Blocking, { emitEvent: false });

          this.dialog.hide();
        }

        if (event.action === 'confirm') {
          const id = this.selectedShield()?.shieldZoneId;

          if (!id) return;

          this.#shieldStore.updateShield({
            id: +shieldId,
            data: {
              dDoSExecutionMode: value,
            },
            customFields: ['ddosShieldMode'],
            toastr: {
              message: `DDoS Mitigation Mode changed to <strong>Logging</strong>.`,
            },
          });
        }
      }),
    );
  }

  private patchDDoSEngineConfig(): void {
    toObservable(this.selectedShield)
      .pipe(filter((shield) => !!shield))
      .subscribe((shield) => {
        // Note: this one has to be always updated because of confirmation modal
        if (shield?.dDoSExecutionMode !== null) {
          this.ddosConfigForm.get('dDoSExecutionMode')?.setValue(shield?.dDoSExecutionMode ?? DDoSExecutionModeEnum.Logging, {
            emitEvent: false,
          });
        }

        if (shield?.dDoSChallengeWindow) {
          this.ddosConfigForm.get('dDoSChallengeWindow')?.setValue(shield?.dDoSChallengeWindow);
        }
      });
  }
}
